import React, { useEffect, useMemo, useState } from 'react';
import './admin.css';
import {
  BarChart3, BookOpen, FileText, LogOut, Mail, MessageSquare, Package,
  RefreshCcw, Search, Settings, ShoppingCart, TicketPercent,
  TrendingUp, Users, ShieldAlert, Star
} from 'lucide-react';
import { ADMIN_PASSWORD, isAdminLoggedIn, loadAdminData, setAdminLoggedIn, loadSiteSettings } from '../lib/compat.js';
import AdminDashboard from './AdminDashboard.jsx';
import AdminOrders from './AdminOrders.jsx';
import AdminBooks from './AdminBooks.jsx';
import AdminStudents from './AdminStudents.jsx';
import AdminCoupons from './AdminCoupons.jsx';
import AdminNotifications from './AdminNotifications.jsx';
import AdminChat from './AdminChat.jsx';
import AdminAnalytics from './AdminAnalytics.jsx';
import AdminSettings from './AdminSettings.jsx';
import AdminRecovery from './AdminRecovery.jsx';
import AdminReviews from './AdminReviews.jsx';

const NAV_PAGES = [
  { id: 'dashboard', label: 'Dashboard', icon: <BarChart3 size={16} /> },
  { id: 'orders', label: 'Orders', icon: <Package size={16} /> },
  { id: 'ebookPurchases', label: 'Ebook - Purchase', icon: <FileText size={16} /> },
  { id: 'recovery', label: 'Paid Recovery', icon: <ShieldAlert size={16} /> },
  { id: 'books', label: 'Books', icon: <BookOpen size={16} /> },
  { id: 'reviews', label: 'Reviews', icon: <Star size={16} /> },
  { id: 'students', label: 'Students', icon: <Users size={16} /> },
  { id: 'coupons', label: 'Coupons', icon: <TicketPercent size={16} /> },
  { id: 'notifications', label: 'Push Notify', icon: <Mail size={16} /> },
  { id: 'chat', label: 'Live Chat', icon: <MessageSquare size={16} /> },
  { id: 'analytics', label: 'Analytics', icon: <TrendingUp size={16} /> },
  { id: 'settings', label: 'Settings', icon: <Settings size={16} /> }
];

const PAGE_TITLES = {
  dashboard: 'Dashboard',
  orders: 'Order History',
  ebookPurchases: 'Ebook - Purchase',
  recovery: 'Paid Order Recovery',
  books: 'Book Catalog',
  reviews: 'Book Reviews',
  students: 'Students',
  coupons: 'Coupon Codes',
  notifications: 'Push Notifications',
  chat: 'Live Chat',
  analytics: 'Analytics',
  settings: 'Site Settings'
};

function orderHasEbookPurchase(order) {
  const fulfillmentType = String(order?.fulfillmentType || '').toLowerCase();
  if (fulfillmentType === 'ebook' || fulfillmentType === 'mixed' || order?.hasEbook === true) return true;
  return (order?.items || []).some((item) => {
    const productType = String(item.productType || '').toLowerCase();
    return productType === 'ebook' || productType === 'combo' || !!item.ebookFileName;
  });
}

export default function AdminWorkspace({ onStore }) {
  const [loggedIn, setLoggedIn] = useState(() => isAdminLoggedIn());
  const [password, setPassword] = useState('');
  const [tab, setTab] = useState('dashboard');
  const [data, setData] = useState({ orders: [], trashedOrders: [], students: [], books: [], coupons: [], settings: {}, notifications: [], paymentRecovery: [], bookRatings: [] });
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [siteSettings, setSiteSettings] = useState({});

  function login(e) {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAdminLoggedIn(true);
      setLoggedIn(true);
      setPassword('');
      setError('');
    } else {
      setError('Incorrect admin password.');
    }
  }

  async function refresh() {
    setLoading(true);
    setError('');
    try {
      const d = await loadAdminData();
      setData(d);
    } catch (e) {
      setError(e.message || 'Unable to load admin data.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!loggedIn) return;
    refresh();
    loadSiteSettings().then(setSiteSettings).catch(() => {});
  }, [loggedIn]);

  function logout() {
    setAdminLoggedIn(false);
    setLoggedIn(false);
  }

  function navigate(pageId) {
    setTab(pageId);
    setQuery('');
  }

  // Derived site info
  const logoUrl = siteSettings.logoUrl || data.settings?.logoUrl || '';
  const faviconUrl = siteSettings.faviconUrl || data.settings?.faviconUrl || '';
  const siteName = siteSettings.siteName || data.settings?.siteName || 'Muppadai Admin';
  const ebookPurchaseData = useMemo(() => ({
    ...data,
    orders: (data.orders || []).filter(orderHasEbookPurchase),
    trashedOrders: (data.trashedOrders || []).filter(orderHasEbookPurchase)
  }), [data]);

  // Apply favicon dynamically when settings load
  useEffect(() => {
    if (!faviconUrl) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
    link.href = faviconUrl;
  }, [faviconUrl]);

  if (!loggedIn) {
    return (
      <div className="adminLoginScreen">
        <form className="adminLoginCard" onSubmit={login}>
          {logoUrl ? (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
              <img src={logoUrl} alt={siteName} style={{ maxWidth: 160, maxHeight: 56, objectFit: 'contain' }} />
            </div>
          ) : (
            <div className="adminLoginMark">ம</div>
          )}
          <h2>Admin Login</h2>
          <p>{siteName}</p>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Admin password" autoComplete="current-password" />
          {error && <div className="dangerText">{error}</div>}
          <button className="btn-primary wide" type="submit">Login →</button>
        </form>
      </div>
    );
  }

  const showSearch = ['orders', 'ebookPurchases', 'students', 'books', 'reviews', 'coupons'].includes(tab);
  const recoveryCount = (data.paymentRecovery || []).filter((r) => !['order_created', 'order_created_by_admin', 'already_exists'].includes(r.status)).length;

  return (
    <div className="adminShell">
      {/* Sidebar */}
      <aside className="adminSidebar">
        <div className="adminSidebarBrand">
          {logoUrl ? (
            <img src={logoUrl} alt={siteName} className="sidebarLogo" />
          ) : (
            <>
              <div className="adminSidebarIcon">ம</div>
              <div>
                <strong>Muppadai Admin</strong>
                <span>Dashboard</span>
              </div>
            </>
          )}
        </div>

        <nav className="adminSidebarNav">
          {NAV_PAGES.map((page) => (
            <button
              key={page.id}
              type="button"
              className={tab === page.id ? 'active' : ''}
              onClick={() => navigate(page.id)}
            >
              {page.icon}
              <span>{page.label}</span>
              {page.id === 'recovery' && recoveryCount > 0 && (
                <span className="navBadge">{recoveryCount}</span>
              )}
            </button>
          ))}
        </nav>

        <button className="adminStoreLink" type="button" onClick={onStore}>
          <ShoppingCart size={15} />
          Storefront
        </button>
      </aside>

      {/* Main */}
      <main className="adminMain">
        {/* Top bar */}
        <header className="adminTopbar">
          <div>
            <div className="adminPageKicker">{NAV_PAGES.find((p) => p.id === tab)?.label}</div>
            <h1 className="adminPageTitle">{PAGE_TITLES[tab]}</h1>
          </div>
          <div className="adminTopbarRight">
            <span className="adminLiveBadge">Live</span>
            {showSearch && (
              <label className="adminSearchBox">
                <Search size={15} />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${tab}…`} />
              </label>
            )}
            <button className="adminIconBtn" type="button" onClick={refresh} title="Refresh data">
              <RefreshCcw size={15} />
            </button>
            <button className="adminIconBtn danger" type="button" onClick={logout} title="Logout">
              <LogOut size={15} />
            </button>
          </div>
        </header>

        {error && <div className="banner danger" style={{ margin: '0 24px 16px' }}>{error}</div>}

        {/* Page content */}
        <div className="adminContent">
          {tab === 'dashboard'      && <AdminDashboard data={data} loading={loading} onRefresh={refresh} />}
          {tab === 'orders'         && <AdminOrders data={data} query={query} onRefresh={refresh} />}
          {tab === 'ebookPurchases' && <AdminOrders data={ebookPurchaseData} query={query} onRefresh={refresh} title="Ebook - Purchase" />}
          {tab === 'recovery'       && <AdminRecovery data={data} onRefresh={refresh} />}
          {tab === 'books'          && <AdminBooks data={data} query={query} onRefresh={refresh} />}
          {tab === 'reviews'        && <AdminReviews data={data} query={query} onRefresh={refresh} />}
          {tab === 'students'       && <AdminStudents data={data} query={query} onRefresh={refresh} />}
          {tab === 'coupons'        && <AdminCoupons data={data} query={query} onRefresh={refresh} />}
          {tab === 'notifications'  && <AdminNotifications data={data} onRefresh={refresh} />}
          {tab === 'chat'           && <AdminChat data={data} />}
          {tab === 'analytics'      && <AdminAnalytics data={data} />}
          {tab === 'settings'       && <AdminSettings data={data} onRefresh={refresh} onSettingsChanged={setSiteSettings} />}
        </div>
      </main>
    </div>
  );
}
